import { gql } from '@apollo/client';

// ----------------------------------------------------------------------
export const HAS_GQL_ERROR = gql`
  query hasAGraphQLError {
    currentGraphQLError @client
  }
`;

export const IS_AUTHENTICATED = gql`
  query isUserAuthenticated {
    isAuthenticated @client
  }
`;

// PII
// ----------------------------------------------------------------------
export const CURRENT_USER = gql`
  query {
    currentUser {
      dob
      email
      firstname
      gender
      lastname
      phoneNumber
      postalCode
      roles
      state
      userId
      location
      prescriberLocations
      signature
    }
  }
`;

export const SEARCH_USERS = gql`
  query searchUsers($userFilter: UserFilterInput!) {
    searchUsers(userFilter: $userFilter) {
      userId
      firstname
      lastname
      preferredName
      email
      dob
      phoneNumber
      gender
      phn
      signature
      location
      roles
      prescriberLocations
      address
      city
      state
      country
      postalCode
    }
  }
`;

export const SEARCH_USER_IDS = gql`
  query searchUserIds($userFilter: UserFilterInput!) {
    searchUsers(userFilter: $userFilter) {
      userId
    }
  }
`;

// APPS
// ----------------------------------------------------------------------
export const CALENDLY_EVENT_ID = gql`
  query calendly($eventId: String) {
    calendlyEventDetails(eventId: $eventId) {
      startTime
      status
    }
  }
`;

export const LOCATIONS = gql`
  query locations {
    locations {
      code
      name
      timezone

      skus {
        shortName
        skuID
      }
      address {
        address1
        city
        state
        country {
          code
          label
        }
      }
      phoneNumber
      calendlyUrl
      referralRequired
    }
  }
`;

export const STUDY_ID_AND_BOOKING_FRAGMENT = gql`
  fragment StudyIDAndBookingFragment on StudyType {
    studyID
    booking {
      utcEnd
      utcStart
    }
  }
`;

export const STUDY_FRAGMENT = gql`
  ${STUDY_ID_AND_BOOKING_FRAGMENT}

  fragment StudyFragment on StudyType {
    ...StudyIDAndBookingFragment
    location
    patient {
      prenuvoID
    }
    status {
      referred
      referredTime
      prescriptionSent
      urgent
      sensitive
      transcribed
      published
      scanned
      booked
      medicalHistory
      useV2Template
    }

    traditionalReportAvailable

    readingPublished {
      author
      contentDate
      reportType
      s3Key
      timestamp
      version
    }

    items
    practitioners
  }
`;

export const INDICATORS = gql`
  query searchStudies($studyFilter: StudyFilterInput!) {
    npdIndicators(filters: $studyFilter) {
      prescriptions {
        indicatorNumber
        color
      }
    }
  }
`;

export const STUDIES = gql`
  ${STUDY_FRAGMENT}

  query searchStudies($studyFilter: StudyFilterInput!) {
    npdStudies(filters: $studyFilter) {
      edges {
        node {
          ...StudyFragment
        }
      }
    }
  }
`;

export const STUDY = gql`
  ${STUDY_FRAGMENT}

  query searchStudy($studyID: UUID!) {
    study(studyId: $studyID) {
      ...StudyFragment
    }
  }
`;

export const PRESCRIPTION_PDF = gql`
  query prescription($studyId: UUID!) {
    prescription(studyId: $studyId) {
      pdf
    }
  }
`;

// HI
// ----------------------------------------------------------------------
export const SAFETY_QUESTIONS = gql`
  query safetyQuestions($studyIds: [String]!, $prescription: Boolean) {
    safetyQuestions(studyIds: $studyIds, prescription: $prescription) {
      state
    }
  }
`;

export const PRESCRIPTIONS = gql`
  query Prescriptions($patientId: String!, $studyId: String) {
    prescriptions(patientId: $patientId, studyId: $studyId) {
      id: prescriptionId
      studyId
      prenuvoId
      prescription
      nursePractitioner
      signature
      prescribedDate
      pickupDate
      pharmacyRepresentativeName
      preferredPharmacy {
        name
        address
        address2
        city
        state
        country
        postalCode
        phoneNumber
        faxNumber
      }
    }
  }
`;

export const NOTES = gql`
  query studyHealthPractitionerNotes($studyId: String!, $noteType: String!) {
    studyHealthPractitionerNotes(studyId: $studyId, noteType: $noteType) {
      id: noteId
      note
      author
      time
      studyId
    }
  }
`;

export const MEDICAL_HISTORY_PDF = gql`
  query medicalHistory($studyId: String!) {
    medicalHistory(studyId: $studyId) {
      pdfFile
    }
  }
`;

// HPD
// ----------------------------------------------------------------------
const NURSE_PRACTITIONER_FRAGMENT = gql`
  fragment NursePractitionerFragment on PractitionerType {
    designation
    firstName
    id
    lastName

    locations {
      edges {
        node {
          id
        }
      }
    }

    mspNumber
    prenuvoId
    profession
  }
`;

export const PRACTITIONER_BY_PRENUVO_ID = gql`
  ${NURSE_PRACTITIONER_FRAGMENT}

  query PractitionerDetailsByPrenuvoId($id: String) {
    practitioner(prenuvoId: $id) {
      ...NursePractitionerFragment
    }
  }
`;

export const PRACTITIONERS_BY_PRENUVO_IDS = gql`
  ${NURSE_PRACTITIONER_FRAGMENT}

  query practitionersByIds($practitionersList: [String]!) {
    practitionersByIds(practitionersList: $practitionersList) {
      edges {
        node {
          ...NursePractitionerFragment
        }
      }
    }
  }
`;
